// src/pages/Orders.tsx — PROC-02: order materials for a job from the console.
// BOM-AUTO-01 (Nov 3, 2026): "Needs a pick" — shopping-list lines made at payment that need a size/finish (or any item) chosen;
//   picking builds the draft PO for that supplier once nothing else is waiting.
// PROC-03 (Nov 3, 2026): "Open supplier orders" lists every order that isn't closed (any step, incl. sent-but-unquoted and
//   print orders); a PO opens the supplier-order panel (edit / revise / re-send / card). A job's PO numbers open it too.
// Find a job → link its property → add material lines → choose branch + delivery or will call → Build & send.
// The quote then comes back to quotes@in.greenmaestro.com and is approved on Today. All writes go through
// ops_* database functions (pricing_admin+) and the build/send edge functions; nothing writes tables directly.
import { useCallback, useEffect, useMemo, useState } from "react";
import { canApply, ops, type BomNeedsPick, type CatalogHit, type OpenProcOrder, type ProcJob, type ProcJobRow, type ProcSupplier } from "../lib/ops";
import { ProcurementPanel } from "../components/ProcurementPanel";   // PROC-03
import { CatalogPicker } from "../components/ProcurementEdit";       // BOM-AUTO-01
import { useOperator, useToast } from "../lib/context";
import { Loading, Notice, PageFrame } from "../components/ui";
import { Panel } from "../components/Panel";
import { SupplyQueue } from "../components/SupplyPanel";   // SUPPLY (record 100)

const day = (d: string | null | undefined) =>
  d ? new Date(d + "T12:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }) : "—";
const usd = (n: number | null | undefined) => (n == null ? "" : `$${Number(n).toFixed(2)}`);

export default function Orders() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<ProcJobRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<ProcJobRow | null>(null);
  // PROC-03: open supplier orders (any step) + the order panel
  const [order, setOrder] = useState<{ id: string; title: string } | null>(null);
  const [openOrders, setOpenOrders] = useState<OpenProcOrder[] | null>(null);
  const loadOpen = useCallback(async () => {
    try { setOpenOrders(await ops.openProcOrders()); } catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void loadOpen(); }, [loadOpen]);
  // BOM-AUTO-01: lines waiting for a pick
  const [picks, setPicks] = useState<BomNeedsPick[] | null>(null);
  const loadPicks = useCallback(async () => {
    try { setPicks(await ops.bomNeedsPick()); } catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void loadPicks(); }, [loadPicks]);

  const search = useCallback(async (term: string) => {
    try { setRows(await ops.procurementJobs(term || undefined)); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, []);
  useEffect(() => { void search(""); }, [search]);

  const panel = order
    ? <ProcurementPanel orderId={order.id} title={order.title} onClose={() => setOrder(null)} onDone={async () => { await loadOpen(); }} />
    : open ? <JobOrderPanel jobId={open.job_id} title={open.service} onClose={() => setOpen(null)} onChanged={() => { void search(q); void loadOpen(); }}
        onOpenOrder={(id, title) => { setOpen(null); setOrder({ id, title }); }} /> : null;

  return (
    <PageFrame title="Orders" lede="Order materials for a job: link the property, add items, choose delivery or will call, send the quote request. Approve quotes on Today." panel={panel}>
      <SupplyQueue onFind={(id) => { setQ(id); void search(id); }} />
      {picks && picks.length > 0 && (
        <NeedsPick rows={picks} onDone={async () => { await loadPicks(); await loadOpen(); }} />
      )}
      {openOrders && openOrders.length > 0 && (
        <section className="order-section">
          <h3>Open supplier orders</h3>
          <ul className="orders-list">
            {openOrders.map((o) => (
              <li key={o.order_id}>
                <button type="button" className="row" onClick={() => { setOpen(null); setOrder({ id: o.order_id, title: `${o.po_number} · ${o.supplier}` }); }}>
                  <span><span className="mono">{o.po_number}</span> · {o.supplier}{o.order_kind === "mail" ? " · print" : ""}{o.test_mode ? " · TEST" : ""}</span>
                  <span className="meta"><strong>{o.status === "draft" && o.status_reason === "revised" ? `revised (rev ${o.revision}), not sent` : o.status.replace(/_/g, " ")}</strong>{o.revision > 1 && o.status !== "draft" ? ` · rev ${o.revision}` : ""}</span>
                  <span className="meta">sent {o.send_count} of 4{o.last_sent_at ? ` · last ${day(o.last_sent_at.slice(0, 10))}` : ""}</span>
                  <span className="meta">{o.card_last4 ? `card …${o.card_last4} ${o.card_status}` : o.payment_status !== "unpaid" ? o.payment_status : ""}</span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      <form className="orders-search" onSubmit={(e) => { e.preventDefault(); void search(q); }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a job: service, GM code, address, email or job id" aria-label="Find a job" />
        <button type="submit" className="btn">Search</button>
      </form>
      {error && <Notice kind="error">{error}</Notice>}
      {!rows && !error && <Loading what="jobs" />}
      {rows && rows.length === 0 && <p className="empty-state">No jobs match.</p>}
      {rows && rows.length > 0 && (
        <ul className="orders-list">
          {rows.map((r) => (
            <li key={r.job_id}>
              <button type="button" className="row" onClick={() => setOpen(r)}>
                <span><strong>{r.service}</strong> · <span className="mono">{r.gm_code ?? "no property"}</span></span>
                <span className="meta">{r.open_orders > 0 ? `${r.open_orders} open order${r.open_orders === 1 ? "" : "s"}` : r.pending_lines > 0 ? `${r.pending_lines} item${r.pending_lines === 1 ? "" : "s"} to order` : ""}</span>
                <span className="meta">{r.address ?? r.zip ?? ""}</span>
                <span className="meta">{r.job_status.replace(/_/g, " ")} · {r.payment_status ?? "—"} · {day(r.scheduled_date)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </PageFrame>
  );
}

function JobOrderPanel(props: { jobId: string; title: string; onClose: () => void; onChanged: () => void; onOpenOrder?: (orderId: string, title: string) => void }) {
  const { role } = useOperator();
  const toast = useToast();
  const mayAct = canApply(role);
  const isOwner = role === "owner";
  const [job, setJob] = useState<ProcJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setJob(await ops.procurementJob(props.jobId)); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, [props.jobId]);
  useEffect(() => { void load(); }, [load]);

  async function act(label: string, fn: () => Promise<void>) {
    setBusy(label); setError(null);
    try { await fn(); await load(); props.onChanged(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  const pendingBySupplier = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of job?.lines ?? []) if (l.status === "pending" && !l.order_ref && l.supplier_id) m.set(l.supplier_id, (m.get(l.supplier_id) ?? 0) + 1);
    return m;
  }, [job]);

  return (
    <Panel kicker="Order materials" title={props.title} onClose={props.onClose}>
      {!job && !error && <Loading what="the job" />}
      {error && <Notice kind="error">{error}</Notice>}
      {job && (
        <>
          <dl className="facts">
            <dt>Job</dt><dd>{job.job_status.replace(/_/g, " ")} · {job.payment_status ?? "unpaid"} · {day(job.scheduled_date)}{job.internal ? " · internal (unpaid)" : ""}</dd>
            <dt>Property</dt>
            <dd>
              {job.property ? <><span className="mono">{job.property.gm_code}</span> · {job.property.address}</>
                : job.link_hint ? <>Not linked. Exact match: <span className="mono">{job.link_hint.gm_code}</span> · {job.link_hint.address}{" "}
                    {mayAct && <button type="button" className="btn" disabled={!!busy} onClick={() => act("link", async () => {
                      const r = await ops.linkJobProperty(job.job_id);
                      if (!r.ok) throw new Error(r.message ?? (r.reason ?? "not linked").replace(/_/g, " "));
                      toast(`✓ Linked to ${r.gm_code}`);
                    })}>{busy === "link" ? "Linking…" : "Link property"}</button>}</>
                : <>Not linked, and the booking address doesn't exactly match a property. Fix the address first; orders need a property for the PO number.</>}
            </dd>
          </dl>

          <section className="order-section">
            <h3>Materials</h3>
            {job.lines.length === 0 && <p className="empty-state">No materials on this job yet.</p>}
            {job.lines.length > 0 && (
              <div className="table-scroll">
                <table className="proc-lines">
                  <thead><tr><th>Item</th><th className="num">Qty</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {job.lines.map((l) => (
                      <tr key={l.id}>
                        <td><span className="mono">{l.sku}</span><br /><span className="muted">{l.name}{l.color ? ` — ${l.color}` : ""}</span></td>
                        <td className="num">{l.qty} {l.unit ?? ""}</td>
                        <td>{l.order_ref ? <span className="mono">{l.order_ref}</span> : l.status.replace(/_/g, " ")}</td>
                        <td>{mayAct && l.status === "pending" && !l.order_ref && (
                          <button type="button" className="btn quiet" disabled={!!busy} onClick={() => act("rm" + l.id, async () => {
                            const r = await ops.bomRemoveLine(l.id); if (!r.ok) throw new Error(r.message ?? "not removed");
                          })}>Remove</button>)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {mayAct && job.suppliers.length > 0 && <AddItem jobId={job.job_id} suppliers={job.suppliers} disabled={!!busy} onAdded={async () => { await load(); props.onChanged(); }} />}
          </section>

          {mayAct && job.property && job.suppliers.filter((s) => pendingBySupplier.get(s.supplier_id)).map((s) => (
            <OrderForm key={s.supplier_id} job={job} supplier={s} lines={pendingBySupplier.get(s.supplier_id) ?? 0} isOwner={isOwner}
              busy={busy} setBusy={setBusy} setError={setError} onDone={async () => { await load(); props.onChanged(); }} />
          ))}

          {job.orders.length > 0 && (
            <section className="order-section">
              <h3>Orders</h3>
              <ul className="proc-checks">
                {job.orders.map((o) => (
                  <li key={o.order_id} className="pass">
                    {props.onOpenOrder
                      ? <button type="button" className="btn quiet mono" onClick={() => props.onOpenOrder!(o.order_id, `${o.po_number} · ${o.supplier}`)}>{o.po_number}</button>
                      : <span className="mono">{o.po_number}</span>}
                    <span>{o.supplier} {o.location ?? ""} · {o.fulfillment === "will_call" ? `will call ${day(o.pickup_on)}` : `delivery ${o.delivery_latest && o.delivery_latest !== o.deliver_on ? `${day(o.deliver_on)}–${day(o.delivery_latest)}` : day(o.deliver_on)}`} · {o.tax_treatment === "own_use" ? "own use (taxed)" : "resale"} · <strong>{o.status.replace(/_/g, " ")}</strong>{o.test_mode ? " · TEST" : ""}</span>
                  </li>
                ))}
              </ul>
              <p className="muted">Quotes that come back appear on Today, where you approve them. Click a PO to edit, re-send or see its card.</p>
            </section>
          )}
        </>
      )}
    </Panel>
  );
}

function AddItem(props: { jobId: string; suppliers: ProcSupplier[]; disabled: boolean; onAdded: () => Promise<void> }) {
  const [supplierId, setSupplierId] = useState(props.suppliers[0]?.supplier_id ?? "");
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<CatalogHit[]>([]);
  const [pick, setPick] = useState<CatalogHit | null>(null);
  const [qty, setQty] = useState("1");
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2 || !supplierId) { setHits([]); return; }
      try { setHits(await ops.catalogSearch(supplierId, q.trim())); } catch (e) { setMsg((e as Error).message); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, supplierId]);

  async function add() {
    if (!pick) return;
    setMsg(null);
    try {
      const r = await ops.bomAddLine(props.jobId, pick.id, Number(qty));
      if (!r.ok) { setMsg(r.message ?? "Not added."); return; }
      setPick(null); setQ(""); setQty("1"); setHits([]);
      await props.onAdded();
    } catch (e) { setMsg((e as Error).message); }
  }

  return (
    <div className="order-form" style={{ marginTop: 10 }}>
      <div className="row2">
        <label>Supplier
          <select value={supplierId} onChange={(e) => { setSupplierId(e.target.value); setPick(null); }}>
            {props.suppliers.map((s) => <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>)}
          </select>
        </label>
        <label>Find an item
          <input value={q} onChange={(e) => { setQ(e.target.value); setPick(null); }} placeholder="Item number, name or color" />
        </label>
      </div>
      {hits.length > 0 && !pick && (
        <ul className="hits">
          {hits.map((h) => (
            <li key={h.id}><button type="button" onClick={() => setPick(h)}>
              <span className="mono">{h.sku}</span> · {h.product_line ?? ""} {h.name}{h.color ? ` — ${h.color}` : ""} <span className="muted">{h.dimensions ?? ""} · {usd(h.price)}/{h.unit}</span>
            </button></li>
          ))}
        </ul>
      )}
      {pick && (
        <div className="row2">
          <p><span className="mono">{pick.sku}</span> · {pick.name}{pick.color ? ` — ${pick.color}` : ""}</p>
          <label>Quantity ({pick.unit})
            <input type="number" min="1" step="1" value={qty} onChange={(e) => setQty(e.target.value)} />
          </label>
        </div>
      )}
      {msg && <Notice kind="error">{msg}</Notice>}
      {pick && <button type="button" className="btn" disabled={props.disabled || !(Number(qty) > 0)} onClick={add}>Add to job</button>}
    </div>
  );
}

function OrderForm(props: {
  job: ProcJob; supplier: ProcSupplier; lines: number; isOwner: boolean; busy: string | null;
  setBusy: (b: string | null) => void; setError: (e: string | null) => void; onDone: () => Promise<void>;
}) {
  const s = props.supplier;
  const toast = useToast();
  const [locationId, setLocationId] = useState(s.default_location_id ?? s.locations[0]?.id ?? "");
  const loc = s.locations.find((l) => l.id === locationId) ?? null;
  const [ful, setFul] = useState<"delivery" | "will_call">("delivery");
  const [deliverOn, setDeliverOn] = useState(props.job.scheduled_date ?? "");
  const [latest, setLatest] = useState("");
  const [win, setWin] = useState("AM (7–11)");
  const [instr, setInstr] = useState(s.default_delivery_instructions ?? "");
  const [pickupOn, setPickupOn] = useState(props.job.scheduled_date ?? "");
  const [pickupWin, setPickupWin] = useState("AM (7–11)");
  const [pickupBy, setPickupBy] = useState("");
  const [internal, setInternal] = useState(props.job.internal);
  const key = "order" + s.supplier_id;

  const canWillCall = !!loc?.will_call, canDeliver = !!loc?.delivery;
  const ready = !!locationId && (ful === "delivery" ? canDeliver && !!deliverOn && (!latest || latest >= deliverOn) : canWillCall && !!pickupOn && pickupBy.trim().length >= 3)
    && (!props.job.internal || (internal && props.isOwner));

  async function go() {
    props.setBusy(key); props.setError(null);
    try {
      const b = await ops.buildOrder({
        job_id: props.job.job_id, supplier_id: s.supplier_id, location_id: locationId, fulfillment: ful,
        ...(ful === "delivery" ? { deliver_on: deliverOn, delivery_latest: latest || undefined, delivery_window: win, delivery_instructions: instr }
                               : { pickup_on: pickupOn, pickup_window: pickupWin, pickup_contact: pickupBy }),
        ...(internal ? { internal_purchase: true } : {}),
      });
      const r = await ops.sendRfq(b.order_id);
      toast(`✓ ${b.po_number} sent to ${r.sent_to}${r.test_mode ? " (TEST)" : ""}`);
      await props.onDone();
    } catch (e) { props.setError((e as Error).message); }
    finally { props.setBusy(null); }
  }

  return (
    <section className="order-section">
      <h3>Order from {s.name} · {props.lines} item{props.lines === 1 ? "" : "s"}{s.test_mode ? " · TEST MODE" : ""}</h3>
      <div className="order-form">
        <label>Branch
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            {s.locations.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.address}, {l.city}{l.hours ? ` · ${l.hours}` : ""}</option>)}
          </select>
        </label>
        <div className="seg" role="group" aria-label="Fulfillment">
          <button type="button" className={ful === "delivery" ? "btn on" : "btn"} disabled={!canDeliver} onClick={() => setFul("delivery")}>Delivery</button>
          <button type="button" className={ful === "will_call" ? "btn on" : "btn"} disabled={!canWillCall} onClick={() => setFul("will_call")}>Will call</button>
        </div>
        {ful === "delivery" ? (
          <>
            <div className="row2">
              <label>Deliver on<input type="date" value={deliverOn} onChange={(e) => setDeliverOn(e.target.value)} /></label>
              <label>…or as late as (optional)<input type="date" value={latest} onChange={(e) => setLatest(e.target.value)} /></label>
            </div>
            <label>Time window<input value={win} onChange={(e) => setWin(e.target.value)} /></label>
            <label>Delivery instructions<textarea className="reason" rows={2} value={instr} onChange={(e) => setInstr(e.target.value)} /></label>
          </>
        ) : (
          <>
            <div className="row2">
              <label>Pickup on<input type="date" value={pickupOn} onChange={(e) => setPickupOn(e.target.value)} /></label>
              <label>Pickup window<input value={pickupWin} onChange={(e) => setPickupWin(e.target.value)} /></label>
            </div>
            <label>Picked up by (name · phone)<input value={pickupBy} onChange={(e) => setPickupBy(e.target.value)} placeholder="Kevin Chavez · (425) 583-5137" /></label>
          </>
        )}
        {props.job.internal && (
          <label className="proc-ack">
            <input type="checkbox" checked={internal} disabled={!props.isOwner} onChange={(e) => setInternal(e.target.checked)} />
            Internal purchase (own property, taxed){props.isOwner ? "" : " — owner only"}
          </label>
        )}
        {s.test_mode && <Notice kind="info">This supplier is in test mode: the quote request goes only to the test address.</Notice>}
        <button type="button" className="btn primary wide" disabled={!!props.busy || !ready} onClick={go}>
          {props.busy === key ? "Building and sending…" : "Build order & send quote request"}
        </button>
      </div>
    </section>
  );
}

// ── BOM-AUTO-01: pick a size/finish (or an item) for shopping-list lines made at payment ──
function NeedsPick(props: { rows: BomNeedsPick[]; onDone: () => Promise<void> }) {
  const { role } = useOperator();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const may = canApply(role);
  async function pick(bomId: string, materialId: string) {
    setBusy(bomId); setErr(null);
    try {
      const r = await ops.bomPick(bomId, materialId);
      const built = (r.orders ?? []).find((o) => o.result === "draft_built");
      toast(`✓ ${r.sku} × ${r.pieces}${built ? ` · draft ${built.po_number} ready in Open supplier orders` : ""}`);
      await props.onDone();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }
  return (
    <section className="order-section">
      <h3>Needs a pick ({props.rows.length})</h3>
      <p className="muted">Made automatically when the customer paid. Pick the exact item; the draft order is built when nothing else for that supplier is waiting.</p>
      {err && <Notice kind="error">{err}</Notice>}
      <ul className="proc-checks">
        {props.rows.map((r) => (
          <li key={r.bom_id} className="flag" style={{ display: "block" }}>
            <div><strong>{r.item ?? "Item"}</strong> · {r.supplier}{r.source_qty != null ? ` · ${r.source_qty} ${r.source_unit ?? ""}` : ""}</div>
            <div className="muted">{r.reason}</div>
            {may && r.options.length > 0 && (
              <div className="row-actions" style={{ flexWrap: "wrap" }}>
                {r.options.map((o) => (
                  <button key={o.id} type="button" className="btn" disabled={!!busy} onClick={() => void pick(r.bom_id, o.id)}
                    title={o.name}>
                    {o.dimensions ?? o.name} · <span className="mono">{o.sku}</span>
                    {r.source_qty && (r.source_unit === "sf" || r.source_unit === "lf") && o.cover_per_unit > 0 ? ` · ${Math.ceil(r.source_qty / o.cover_per_unit)} pcs` : ""}
                    {o.price != null ? ` · $${Number(o.price).toFixed(2)}` : ""}
                  </button>
                ))}
              </div>
            )}
            {may && r.options.length === 0 && (
              <CatalogPicker supplierId={r.supplier_id} disabled={!!busy} onAdd={(materialId) => pick(r.bom_id, materialId)} />
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
